import { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Edit, Trash2, Cat, Save } from 'lucide-react';
import ImagePicker from '@/components/ImagePicker';

const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api` 
  : '/api';

export function AdminCatsList() {
  const { getAuthHeader } = useAuth();
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCats();
  }, []);

  const fetchCats = async () => {
    try {
      const response = await fetch(`${API}/cats`);
      if (response.ok) {
        const data = await response.json();
        setCats(data);
      }
    } catch (error) {
      console.error('Error:', error);
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce chat ?')) return;
    try {
      const response = await fetch(`${API}/admin/cats/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': getAuthHeader() }
      });
      if (response.ok) {
        toast.success('Chat supprimé');
        fetchCats();
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  return (
    <div className="min-h-screen bg-cream">
      <header className="bg-forest text-white py-4 px-6">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard" className="hover:text-sand">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="font-heading text-xl font-bold">Nos Chats</h1>
          </div>
          <Link to="/admin/cats/new">
            <Button className="bg-autumn hover:bg-autumn-dark" data-testid="add-cat-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nouveau chat
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        {loading ? (
          <p className="text-center text-forest/60">Chargement...</p>
        ) : cats.length === 0 ? (
          <div className="text-center py-12">
            <Cat className="w-12 h-12 text-forest/30 mx-auto mb-4" />
            <p className="font-body text-forest/60">Aucun chat enregistré</p>
            <Link to="/admin/cats/new" className="text-autumn hover:underline mt-2 inline-block">
              Ajouter le premier chat
            </Link>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {cats.map((cat) => (
              <div key={cat.id} className="bg-white rounded-xl overflow-hidden shadow-soft" data-testid={`admin-cat-card-${cat.name.toLowerCase()}`}>
                {cat.images?.[0] && (
                  <img src={cat.images[0]} alt={cat.name} className="w-full h-40 object-cover" />
                )}
                <div className="p-4">
                  <h3 className="font-heading font-semibold text-forest">{cat.name}</h3>
                  <p className="font-body text-sm text-forest/60">
                    {cat.role} - {cat.color}
                  </p>
                  <p className="font-body text-xs text-forest/50 mt-1">
                    {cat.images?.length || 0} photo(s)
                  </p>
                  <div className="flex gap-2 mt-3">
                    <Link to={`/admin/cats/edit/${cat.id}`} className="flex-1">
                      <Button variant="outline" size="sm" className="w-full">
                        <Edit className="w-4 h-4 mr-1" /> Modifier
                      </Button>
                    </Link>
                    <Button variant="outline" size="sm" onClick={() => handleDelete(cat.id)} className="text-red-500 hover:bg-red-50">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

export function AdminCatsForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  
  const [formData, setFormData] = useState({
    name: '',
    role: 'Femelle reproductrice',
    color: '',
    birth_date: '',
    character: '',
    description: '',
    images: [],
    order: 0,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isEdit) {
      fetchCat();
    }
  }, [id]);

  const fetchCat = async () => {
    try {
      const response = await fetch(`${API}/cats`);
      if (response.ok) {
        const data = await response.json();
        const cat = data.find(c => c.id === id);
        if (cat) setFormData(cat);
      }
    } catch (error) {
      toast.error('Erreur lors du chargement');
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const url = isEdit ? `${API}/admin/cats/${id}` : `${API}/admin/cats`;
      const method = isEdit ? 'PUT' : 'POST';
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': getAuthHeader(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      if (response.ok) {
        toast.success(isEdit ? 'Chat mis à jour' : 'Chat ajouté');
        navigate('/admin/cats');
      } else {
        toast.error('Erreur lors de la sauvegarde');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-cream">
      <header className="bg-forest text-white py-4 px-6">
        <div className="max-w-3xl mx-auto flex items-center gap-4">
          <Link to="/admin/cats" className="hover:text-sand">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <h1 className="font-heading text-xl font-bold">
            {isEdit ? 'Modifier le chat' : 'Nouveau chat'}
          </h1>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8">
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-6 shadow-soft space-y-6">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="font-body font-semibold text-forest">Nom *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="mt-1"
                placeholder="Ex: Sia"
                required
                data-testid="cat-name-input"
              />
            </div>
            <div>
              <Label className="font-body font-semibold text-forest">Rôle *</Label>
              <Input
                value={formData.role}
                onChange={(e) => setFormData({...formData, role: e.target.value})}
                className="mt-1"
                placeholder="Ex: Maman, Papa"
                required
              />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="font-body font-semibold text-forest">Couleur *</Label>
              <Input
                value={formData.color}
                onChange={(e) => setFormData({...formData, color: e.target.value})}
                className="mt-1"
                placeholder="Ex: Brown Blotched Tabby"
                required
              />
            </div>
            <div>
              <Label className="font-body font-semibold text-forest">Date de naissance</Label>
              <Input
                value={formData.birth_date}
                onChange={(e) => setFormData({...formData, birth_date: e.target.value})}
                className="mt-1"
                placeholder="Ex: 29/11/2021"
              />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="font-body font-semibold text-forest">Caractère</Label>
              <Input
                value={formData.character}
                onChange={(e) => setFormData({...formData, character: e.target.value})}
                className="mt-1"
                placeholder="Ex: Maline et câline"
              />
            </div>
            <div>
              <Label className="font-body font-semibold text-forest">Ordre d'affichage</Label>
              <Input
                type="number"
                value={formData.order}
                onChange={(e) => setFormData({...formData, order: parseInt(e.target.value) || 0})}
                className="mt-1"
              />
            </div>
          </div>

          <div>
            <Label className="font-body font-semibold text-forest">Description</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              className="mt-1 min-h-[120px]"
              placeholder="Description du chat..."
            />
          </div>

          {/* Images management */}
          <div>
            <ImagePicker
              label="Photos du chat"
              value={formData.images}
              onChange={(images) => setFormData({...formData, images})}
              multiple
            />
          </div>

          <div className="flex gap-4 pt-4">
            <Button type="submit" disabled={loading} className="bg-forest hover:bg-forest-dark" data-testid="save-cat-btn">
              <Save className="w-4 h-4 mr-2" />
              {loading ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
            <Link to="/admin/cats">
              <Button type="button" variant="outline">Annuler</Button>
            </Link>
          </div>
        </form>
      </main>
    </div>
  );
}
